
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import ProductTour from 'src/components/docs/ProductTour/ProductTour';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Learn about the Link returning user experience, which simplifies the onboarding experience for users who have already linked a financial institution with Plaid',
  preloadImages: [{
    src: '/assets/img/docs/remember-me-first-time.png',
    type: 'image/webp'
  }]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const Image = makeShortcode("Image");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='The returning user experience' subtitle='Learn how Plaid streamlines the user experience for returning users' mdxType="Header" />
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`The returning user experience (formerly known as Remember Me) streamlines onboarding for users who have already connected a financial account with Plaid in the US, UK, and Canada. In Link, users can choose to associate their phone number with the accounts they're connecting to a financial app or service. Once users have opted in to being 'remembered' by Plaid, they'll be able to quickly connect those same accounts to other financial apps and services in the future using a one-time password for thousands of financial institutions, resulting in higher conversion and a simpler user experience.`}</p>
    <p>{`On the Consent screen in Link, users can input their phone number and verify it using a one-time password sent to their device. Next, the user will proceed to select an institution and connect their account(s). Once account verification is completed, Plaid will associate the institution and accounts with the user's phone number. If a user has previously connected an account, Link may streamline the flow by reusing an existing connection and taking the user directly to account selection. If Link can't reuse that connection, the user will go through the full Link flow to reconnect.`}</p>
    <p>{`The returning user experience is automatically enabled for all eligible customers, and you do not need to make any updates to your integration to support it.`}</p>
    <Callout mdxType="Callout">
      <p>{`The legacy returning user experience flows (Institution Boosting, Pre-Matched RUX, and Pre-Authenticated RUX) have been replaced by the revamped returning user experience (formerly known as the Remember Me flow) as of October 28, 2024. For questions or more details, contact your Plaid account manager.`}</p>
    </Callout>
    <h5 {...{
      "id": "the-returning-user-experience-for-a-new-user"
    }}>{`The returning user experience for a new user`}</h5>
    <ProductTour steps={[{
      text: 'The user starts a Link session and enters their phone number.',
      image: '/assets/img/docs/rux/rux_tour/rux-comcon.png'
    }, {
      text: 'Plaid does not recognize this number as having connected to Plaid before. The user is prompted to confirm their number through an SMS code.',
      image: '/assets/img/docs/rux/rux_new/rux_new_3.png'
    }, {
      text: 'The user selects an institution to connect with.',
      image: '/assets/img/docs/rux/rux_new/rux_new_4.png'
    }, {
      text: 'They enter their credentials, or proceed through an OAuth flow.',
      image: '/assets/img/docs/rux/rux_new/rux_new_5.png'
    }, {
      text: '...and they select their account.',
      image: '/assets/img/docs/rux/rux_new/rux_new_6.png'
    }, {
      text: 'This user has successfully connected to this institution, and it\'s now available for them to connect to the next time they use Plaid Link.',
      image: '/assets/img/docs/rux/rux_new/rux_new_7.png'
    }]} mdxType="ProductTour" />
    <h5 {...{
      "id": "the-returning-user-experience-for-a-saved-user"
    }}>{`The returning user experience for a saved user`}</h5>
    <ProductTour steps={[{
      text: 'The user starts a Link session and enters their phone number.',
      image: '/assets/img/docs/rux/rux_tour/rux-comcon.png'
    }, {
      text: 'Plaid recognizes this number as one that\'s connected to Plaid before, and asks the user to confirm a code sent by SMS.',
      image: '/assets/img/docs/rux/rux_new/rux_new_3.png'
    }, {
      text: 'The user can then instantly connect to an institution that they\'ve previously connected to with Plaid.',
      image: '/assets/img/docs/rux/rux_tour/rux_4.png'
    }, {
      text: 'They can also choose which saved accounts to connect to your app.',
      image: '/assets/img/docs/rux/rux_tour/rux_5.png'
    }]} mdxType="ProductTour" />
    <h5 {...{
      "id": "returning-user-experience"
    }}>{`Returning user experience`}</h5>
    <p>{`When users want to connect their saved institutions and accounts to additional Plaid-powered apps or services, Plaid runs security checks to detect that they are a returning user with the same phone number and device.`}</p>
    <p>{`Users can choose to be remembered by Plaid, making future connections faster and easier. To enroll, the user will enter their phone number in Link. This flow is available for phone numbers with country codes +1 (covering the United States, Puerto Rico, and Canada), +44 (UK), and +52 (Mexico); users selecting codes from any other country will be redirected to the standard Link flow. The default country shown in the dropdown will be based on the country code(s) provided in the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` call; for example, if `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` is called with a single country code, that country will be shown as the default; if it is called with multiple countries, including `}<inlineCode parentName="p">{`US`}</inlineCode>{`, the United States will be shown as the default.`}</p>
    <p>{`The phone number's country determines whether Plaid creates a profile; only US, UK, and Canadian institutions can be saved to a Plaid profile. `}</p>
    <h4 {...{
      "id": "pre-filling-phone-numbers-for-faster-account-verification"
    }}>{`Pre-filling phone numbers for faster account verification`}</h4>
    <p>{`Link sessions can be enabled for a more streamlined user experience when Plaid already knows the user's phone number. When a `}<inlineCode parentName="p">{`user.phone_number`}</inlineCode>{` is provided via `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`, Plaid will pre-fill the phone number in Link for the user. Only the last 4 digits of the associated phone number are shown in order to preserve user privacy. `}</p>
    <p>{`The user can then verify their phone number. Plaid will deploy a number of security checks to verify that the phone number belongs to the device before the user can select which saved institution to connect to an app or service. Pre-filling phone numbers can help boost conversion while reducing the number of manual inputs from users.`}</p>
    <h4 {...{
      "id": "bypassing-phone-number-input"
    }}>{`Bypassing phone number input`}</h4>
    <p>{`When Plaid recognizes a returning user before the phone number entry screen, Link can skip the consent and phone number input screens entirely and take the user directly to a one-time password (OTP) verification screen. This builds on `}<a parentName="p" {...{
        "href": "#pre-filling-phone-numbers-for-faster-account-verification"
      }}>{`pre-filling phone numbers`}</a>{`: rather than showing the phone number for the user to confirm, Link removes the phone number step altogether for users Plaid can already recognize, further reducing manual inputs and boosting conversion.`}</p>
    <p>{`The OTP screen shown in this flow has a distinct design and includes a "Not you?" option. Selecting it returns the user to the standard flow.`}</p>
    <Image src='/assets/img/docs/rux/bypass-phone-number-otp.png' alt='The distinct OTP verification screen shown when Link bypasses phone number input for a recognized returning user, including a "Not you?" link.' width='280' height='594' size='small' expandable={true} mdxType="Image" />
    <p>{`This experience is automatically applied for eligible sessions, and you do not need to make any updates to your integration to support it. Providing a `}<inlineCode parentName="p">{`user.phone_number`}</inlineCode>{` via `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` increases the likelihood that a returning user can be recognized and skip phone number input.`}</p>
    <Callout mdxType="Callout">
      <p>{`You can test this experience in Sandbox by passing the returning user phone number `}<inlineCode parentName="p">{`415-555-0011`}</inlineCode>{` as `}<inlineCode parentName="p">{`user.phone_number`}</inlineCode>{` in `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`. Link will open directly on the OTP verification screen. See `}<a parentName="p" {...{
          "href": "#testing-in-sandbox"
        }}>{`Testing in Sandbox`}</a>{` for the full list of seeded Sandbox phone numbers.`}</p>
    </Callout>
    <h4 {...{
      "id": "passkeys"
    }}>{`Passkeys`}</h4>
    <p>{`Passkeys are a secure alternative to passwords and can provide a streamlined account linking experience for thousands of institutions. Plaid will automatically enable passkeys for Link sessions on iOS devices when you use the Plaid iOS SDK 4.3.0+, React Native SDK 10.2.0+, or Flutter SDK 1.0.0+.`}</p>
    <p>{`In Link, users will be prompted to enable passkeys with Face or Touch ID before proceeding with connecting their financial accounts to a chosen app or service. When the same user wants to connect to another app or service, they can use their passkey with Face or Touch ID instead of a one-time password for a streamlined authentication experience. `}</p>
    <Image src='/assets/img/docs/rux/passkeys.png' alt="Plaid passkey flow for returning users: Connect with customer app, input phone, log in with Face/Touch ID, select saved account." expandable={true} mdxType="Image" />
    <h4 {...{
      "id": "plaid-account"
    }}>{`Plaid Account`}</h4>
    <p>{`The Plaid Account (formerly known as Plaid Passport) provides a streamlined experience where end users can opt-in to saving their financial account connections and personal information in a Plaid Account. They can then securely and easily share this account with some Plaid-powered apps and services. `}</p>
    <p>{`Plaid Account is available now for `}<a parentName="p" {...{
        "href": "/docs/check"
      }}>{`Consumer Report by Plaid Check`}</a>{`. End users can use their Plaid Account to share their consumer report with Plaid Check customers and partners to receive credit-related services. This experience is automatically enabled for eligible customers, and you do not need to make any updates to your integration to support it. `}</p>
    <p>{`To view the Plaid Account experience, you can launch Link with any `}<a parentName="p" {...{
        "href": "/docs/check"
      }}>{`Plaid Check`}</a>{` product in the Sandbox environment. `}</p>
    <h4 {...{
      "id": "silent-network-authentication"
    }}>{`Silent Network Authentication`}</h4>
    <p>{`Silent Network Authentication (SNA) is an aspect of the mobile returning user experience that enables Plaid to verify a consumer's phone number using their mobile network carrier. Eligible users can skip manual OTP verification of their phone number, which removes one step in the Link flow. SNA is currently used for all eligible Layer and Plaid Check Consumer Report flows. `}</p>
    <p>{`SNA is supported on mobile flows using Plaid's native, React Native, or Flutter mobile SDKs using Android SDK version >=4.1.1 and iOS SDK version >=5.6.0. Customers using Layer or Plaid Check Consumer Report are strongly recommended to upgrade to iOS SDK version >=6.0.1, React Native iOS >=12.0.2, or Flutter 1.0.0+ in order to take advantage of substantial performance improvements to the SNA flow. Aside from using a compatible SDK, no work is required on your part to enable SNA.`}</p>
    <p>{`SNA is compatible with AT&T, T-Mobile, and Verizon, and works by verifying the user's SIM is connected to the mobile network carrier and not spoofed or cloned. If SNA cannot verify the user's number (for example, because verification failed, their network is not compatible, or mobile data is unavailable) Plaid will fall back to the manual OTP flow. `}</p>
    <p>{`SNA is available only in Production and cannot currently be emulated in Sandbox.`}</p>
    <h4 {...{
      "id": "testing-in-sandbox"
    }}>{`Testing in Sandbox`}</h4>
    <p>{`The returning user flow can be tested in the Sandbox or Production environments.`}</p>
    <p>{`Real phone numbers do not work in Sandbox. Instead, a returning user is represented by a `}<a parentName="p" {...{
        "href": "/docs/sandbox/profiles/"
      }}>{`Sandbox Profile`}</a>{`: a saved phone number with identity data and connected Items, scoped to your Plaid team. For all Sandbox profiles, the OTP is `}<inlineCode parentName="p">{`123456`}</inlineCode>{`.`}</p>
    <p>{`To test the flow, launch Link and enter a profile's phone number, or launch Link directly from the `}<strong parentName="p">{`Profiles`}</strong>{` tab of `}<a parentName="p" {...{
        "href": "/docs/sandbox/studio/"
      }}>{`Sandbox Studio`}</a>{`. Plaid provides starter profiles that already have connected accounts:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Sandbox Profile`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Phone number`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Starting state`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Default Returning User`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`415-555-0136`}</inlineCode>{` or `}<inlineCode parentName="td">{`415-555-0011`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Two saved institutions`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Trusted Multi-item`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`415-555-0137`}</inlineCode>{` or `}<inlineCode parentName="td">{`415-555-0012`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Three saved institutions`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`OAuth Multi-item`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`415-555-0138`}</inlineCode>{` or `}<inlineCode parentName="td">{`415-555-0013`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Four saved institutions, including an OAuth institution`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`Starter profiles shaped for specific use cases -- a college student, a small business owner, a retiree, an affluent investor, and a risk review case -- are listed in `}<a parentName="p" {...{
        "href": "/docs/sandbox/profiles/#starter-profiles"
      }}>{`Sandbox Profiles`}</a>{`.`}</p>
    <p>{`To test a new user, use any valid US phone number that has not been used in Link before. To build a returning user from your own `}<a parentName="p" {...{
        "href": "/docs/sandbox/user-custom/"
      }}>{`custom test data`}</a>{`, use a new phone number and connect a custom user through Link; the profile keeps the institutions you connect to it.`}</p>
    <p>{`Trusted-device state on a starter profile is visible through the API but is not matched against your browser's device fingerprint, so saved accounts still prompt for authentication in Sandbox. See `}<a parentName="p" {...{
        "href": "/docs/sandbox/#differences-between-sandbox-and-production"
      }}>{`Differences between Sandbox and Production`}</a>{`.`}</p>
    <h5 {...{
      "id": "legacy-seeded-phone-numbers"
    }}>{`Legacy seeded phone numbers`}</h5>
    <p>{`Before Sandbox Profiles, returning user testing used phone numbers seeded globally across all Plaid teams. These still work and are unchanged, but they are shared between teams, cannot be reset, and cannot be extended with new institutions. New integrations should use the starter profiles above.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Scenario`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Seeded phone number`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`New User`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`415-555-0010`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Verified Returning User + new device`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`415-555-0014`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Verified Returning User: automatic account selection`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`415-555-0015`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Verified Returning User (Canada)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`416-555-0022`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "tracking-events"
    }}>{`Tracking events`}</h4>
    <p>{`Link emits events to indicate whether or not users opt in to being remembered by Plaid:`}</p>
    <h5 {...{
      "id": "events"
    }}>{`Events`}</h5>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Event`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Meaning`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SUBMIT_PHONE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`User has provided their phone number to be remembered by Plaid`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SUBMIT_OTP`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`User has entered an OTP code to verify their phone number`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`AUTO_SUBMIT_PHONE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`User was automatically sent an OTP code without a UI prompt, bypassing manual phone number entry`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`VERIFY_PHONE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`User has successfully verified their phone number`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SKIP_SUBMIT_PHONE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`User chose not to provide their phone number to be remembered by Plaid`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`CONNECT_NEW_INSTITUTION`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`User chose to connect a new institution`}</td>
        </tr>
      </tbody>
    </table>
    <h5 {...{
      "id": "view-names"
    }}>{`View names`}</h5>
    <p>{`The following can be found in the `}<inlineCode parentName="p">{`view_name`}</inlineCode>{` field in the `}<inlineCode parentName="p">{`TRANSITION_VIEW`}</inlineCode>{` event for returning user panes:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`View name`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Meaning`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SUBMIT_PHONE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`User was prompted to provide their phone number to be remembered by Plaid`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`VERIFY_PHONE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`User was prompted to verify their phone number`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SELECT_SAVED_ACCOUNT`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`User was prompted to select the underlying account from the saved Item`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SELECT_SAVED_INSTITUTION`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`User was prompted to select one of multiple saved Items`}</td>
        </tr>
      </tbody>
    </table>
    <h5 {...{
      "id": "match-reasons"
    }}>{`Match reasons`}</h5>
    <p>{`The `}<inlineCode parentName="p">{`match_reason`}</inlineCode>{` field in the `}<inlineCode parentName="p">{`SELECT_INSTITUTION`}</inlineCode>{` event has the following values for the returning user flow:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Match reason`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Meaning`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`AUTO_SELECT_SAVED_INSTITUTION`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The `}<inlineCode parentName="td">{`SELECT_SAVED_INSTITUTION`}</inlineCode>{` pane was skipped`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SAVED_INSTITUTION`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`User selected a saved institution`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SAVED_ACCOUNT`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`User selected a saved account`}</td>
        </tr>
      </tbody>
    </table>
    <h5 {...{
      "id": "error-events"
    }}>{`Error events`}</h5>
    <p>{`Link will emit the `}<inlineCode parentName="p">{`ERROR`}</inlineCode>{` event when the user submits an invalid phone number or an invalid OTP. The `}<inlineCode parentName="p">{`error_code`}</inlineCode>{` will be `}<inlineCode parentName="p">{`INVALID_PHONE_NUMBER`}</inlineCode>{` or `}<inlineCode parentName="p">{`INVALID_OTP`}</inlineCode>{`, respectively.`}</p>
    <p>{`For more details, see `}<a parentName="p" {...{
        "href": "/docs/link/web/"
      }}>{`Link SDK documentation`}</a>{`. For more information on tracking Link conversion in general, see `}<a parentName="p" {...{
        "href": "/docs/link/best-practices/#improving-link-conversion"
      }}>{`Improving Link conversion`}</a>{`.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "pre-filling-phone-numbers-for-faster-account-verification",
        level: 4,
        title: "Pre-filling phone numbers for faster account verification",
      },
{
        id: "bypassing-phone-number-input",
        level: 4,
        title: "Bypassing phone number input",
      },
{
        id: "passkeys",
        level: 4,
        title: "Passkeys",
      },
{
        id: "plaid-account",
        level: 4,
        title: "Plaid Account",
      },
{
        id: "silent-network-authentication",
        level: 4,
        title: "Silent Network Authentication",
      },
{
        id: "testing-in-sandbox",
        level: 4,
        title: "Testing in Sandbox",
      },
{
        id: "tracking-events",
        level: 4,
        title: "Tracking events",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
